import * as endpoints from "../../lib/api/endpoints";
import * as queryStringHelpers from "../../lib/queryStringHelpers";
import * as routes from "../../constants/routes";
import * as validationLib from "../../lib/validation";
import ActionButton, {ActionButtonStyle} from "../ActionButton";
import BoxInput from "../BoxInput";
import CardBanner from "../CardBanner";
import ErrorBoundary from "../ErrorBoundary";
import getFirstInvalidKey, {ValidationType} from "../../lib/getFirstInvalidKey";
import hasFailingValidation from "../../lib/hasFailingValidation";
import IdleTimeoutWrapper from "../IdleTimeoutWrapper";
import maskDateInput from "../../lib/maskDateInput";
import masks from "../../constants/masks";
import React, {useEffect, useRef, useState} from "react";
import securedCardImage from "../../images/secured.png";
import securedCardImage2 from "../../images/secured@2x.png";
import securedCardImage3 from "../../images/secured@3x.png";
import SetNoIndex from "../../lib/SetNoIndex";
import styles from "./SecuredCardWelcomeBack.module.css";
import useFocusInvalid from "../../lib/useFocusInvalid";
import validateLookupAppField from "../../lib/validateLookupAppField";
import ValidationList from "../ValidationList";
import {
    ApplicationStatus,
    LookupAppInfo,
    LookupAppKeys,
    LookupAppValidation,
} from "../../lib/api/models";
import {RouteComponentProps} from "react-router-dom";

const strings = {
    dateOfBirth: "Date of Birth (MM/DD/YYYY)",
    genericError:
        "Please verity your SSN and Birth Date are correct and try again. If you believe you’ve reached this in error, your secured card offer may have expired.",
    lastFour: "Last 4 of SSN",
    next: "Next",
    pleaseEnterSSN:
        "Please enter the last 4 digits of your Social Security Number and Date of Birth to continue",
    timeoutString: "Check your email or watch for mail from 1st Financial Bank USA.",
    welcomeBack: "Welcome back to your 1st Financial Bank Secure Card application!",
};

const EMPTY_LOOKUP_INFO: LookupAppInfo = {
    emailToken: "",
    dob: "",
    ssnLast4: "",
};

const EMPTY_LOOKUP_VALIDATION: LookupAppValidation = {};

export default function SecuredCardWelcomeBack(props: RouteComponentProps) {
    const queryParams = queryStringHelpers.objectify(window.location.search);

    const [lookupAppInfo, setLookupAppInfo] = useState({
        ...EMPTY_LOOKUP_INFO,
        emailToken: queryParams.token ? queryParams.token : "",
    });
    const [loading, setLoading] = useState(false);
    const [validation, setValidation] = useState<validationLib.Validation>({});
    const formValidation = validation.errors ?? EMPTY_LOOKUP_VALIDATION;
    const [validationCount, setValidationCount] = useState(1);

    const isMountedRef = useRef<boolean>(false);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    SetNoIndex();

    function clearValidation(validationKey: LookupAppKeys) {
        setValidation((prev) => {
            if (prev.generic || (prev.errors && prev.errors[validationKey])) {
                const nextValidation = {
                    ...prev,
                };
                delete nextValidation.generic;
                if (prev && prev.errors) {
                    delete prev.errors[validationKey];
                }
                return nextValidation;
            }
            return prev;
        });
    }

    function passesSsnValidation() {
        const {isValid, messages, validationKey} = validateLookupAppField(
            LookupAppKeys.SsnLast4,
            lookupAppInfo[LookupAppKeys.SsnLast4],
        );
        if (isValid) {
            return true;
        }
        if (validationKey !== null) {
            setValidation((prev) => ({
                ...prev,
                errors: {
                    ...prev?.errors,
                    [validationKey]: messages,
                },
            }));
        }

        return false;
    }

    function passesDobValidation() {
        const {isValid, messages, validationKey} = validateLookupAppField(
            LookupAppKeys.DateOfBirth,
            lookupAppInfo[LookupAppKeys.DateOfBirth],
        );
        if (isValid) {
            return true;
        }
        if (validationKey !== null) {
            setValidation((prev) => ({
                ...prev,
                errors: {
                    ...prev?.errors,
                    [validationKey]: messages,
                },
            }));
        }
        return false;
    }

    /* form submission client side validation */
    function passesClientValidation(): boolean {
        const passesSSN = passesSsnValidation();
        const passesDOB = passesDobValidation();
        return passesSSN && passesDOB;
    }

    function onChangeInfo(field: LookupAppKeys, value: string) {
        setLookupAppInfo((prev) => ({
            ...prev,
            [field]: value,
        }));
        clearValidation(field);
    }

    function onSubmit(e: React.FormEvent) {
        e.preventDefault();

        if (!passesClientValidation() || hasFailingValidation(validation)) {
            return;
        }

        setLoading(true);
        endpoints
            .getApplication(lookupAppInfo)
            .then((data) => {
                if (!isMountedRef.current) {
                    return;
                }
                const status: string = data.status;
                const applicationId: string | null = data.applicationId;
                let queryString = window.location.search;

                if (!queryParams.dsn && applicationId) {
                    queryString = queryStringHelpers.addDSN(queryString, applicationId);
                }

                switch (status) {
                    case ApplicationStatus.SecuredAccepted:
                        props.history.push(`${routes.SECURED_DEPOSIT}${queryString}`);
                        break;
                    default:
                        window.location.replace(
                            `${process.env.REACT_APP_BASE_PATH}${routes.SECURED_CARD}${queryString}`,
                        );
                }
            })
            .catch((err) => {
                if (!isMountedRef.current) {
                    return;
                }
                const errValidation = validationLib.getValidationObj(err);
                errValidation.generic = [strings.genericError];
                setValidation(errValidation);
            })
            .finally(() => {
                if (!isMountedRef.current) {
                    return;
                }
                setLoading(false);
                setValidationCount((prev) => prev + 1);
            });
    }

    function getSecuredCardImage() {
        return (
            <img
                className={styles.cardsImage}
                src={securedCardImage}
                srcSet={`${securedCardImage} 1x, ${securedCardImage2} 2x, ${securedCardImage3} 3x`}
                alt="secured card"
            />
        );
    }

    function handleChangeWithMask(e: React.ChangeEvent<HTMLInputElement>) {
        const value = e.target.value;
        onChangeInfo(LookupAppKeys.DateOfBirth, maskDateInput(value));
    }

    const socialSecurityRef = useRef<HTMLInputElement>(null);
    const dateOfBirthRef = useRef<HTMLInputElement>(null);

    const firstInvalidKey = getFirstInvalidKey(validation, ValidationType.SecuredWelcomeBack);
    const firstInvalidRef =
        firstInvalidKey === LookupAppKeys.SsnLast4 ? socialSecurityRef : dateOfBirthRef;
    useFocusInvalid(validationCount, firstInvalidRef?.current);

    return (
        <ErrorBoundary>
            <IdleTimeoutWrapper
                focusAfterContinue={null}
                noLink={true}
                description={strings.timeoutString}
            >
                <CardBanner
                    cardImage={getSecuredCardImage()}
                    renderInterestRates={false}
                    renderOnlyCard={false}
                >
                    <>
                        <div className={styles.welcomeBack}>{strings.welcomeBack}</div>
                        <div className={styles.ssnText}>{strings.pleaseEnterSSN}</div>
                        <form className={styles.actions} onSubmit={onSubmit}>
                            <BoxInput
                                className={styles.socialSecurity}
                                name={LookupAppKeys.SsnLast4}
                                value={lookupAppInfo[LookupAppKeys.SsnLast4]}
                                onChange={(e) =>
                                    onChangeInfo(LookupAppKeys.SsnLast4, e.target.value)
                                }
                                label={strings.lastFour}
                                placeholder={strings.lastFour}
                                minLength={4}
                                maxLength={4}
                                pattern="\d{4}"
                                ref={socialSecurityRef}
                                required={true}
                                inputMode="numeric"
                                validationMessage={formValidation[LookupAppKeys.SsnLast4]}
                            />
                            <BoxInput
                                className={styles.dateOfBirth}
                                name={LookupAppKeys.DateOfBirth}
                                value={lookupAppInfo[LookupAppKeys.DateOfBirth]}
                                onChange={handleChangeWithMask}
                                label={strings.dateOfBirth}
                                placeholder={strings.dateOfBirth}
                                maxLength={masks.dateOfBirth.pattern.length}
                                pattern={masks.dateOfBirth.regex}
                                ref={dateOfBirthRef}
                                required={true}
                                inputMode="numeric"
                                validationMessage={formValidation[LookupAppKeys.DateOfBirth]}
                            />
                            {validation.generic ? (
                                <ValidationList
                                    className={styles.validation}
                                    messages={validation.generic}
                                />
                            ) : null}
                            <ActionButton
                                className={styles.next}
                                style={ActionButtonStyle.secondary}
                                label={strings.next}
                                loading={loading}
                                fullWidth={true}
                                type="submit"
                            />
                        </form>
                    </>
                </CardBanner>
            </IdleTimeoutWrapper>
        </ErrorBoundary>
    );
}
